<template>
  <div v-if="showUpgradeCta" class="upgrade-cta">
    <div class="upgrade-cta__copy">
      <span class="upgrade-cta__title">{{ ctaTitle }}</span>
      <span class="upgrade-cta__desc">{{ ctaDesc }}</span>
    </div>
    <Button type="button" size="sm" class="upgrade-cta__btn" @click="handleOpenDialogClick">
      {{ ctaAction }}
      <ArrowRight class="size-3.5" />
    </Button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { ArrowRight } from '@lucide/vue'
import { useRoute, useRouter } from 'vue-router'
import { Button } from '@/components/ui/button'
import { useUserStore } from '@/manage/stores/user'
import { preferenceStorage } from '@/utils/storage/preferences'
import { usePostHog } from '@/shared/composables/usePostHog'

const OPEN_UPGRADE_DIALOG_EVENT = 'easyhire:open-upgrade-dialog'
const UPGRADE_DEADLINE_CAMPAIGN_ID = 'pricing-refresh-2026-05-03'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { asset, assetIsVip } = storeToRefs(userStore)
const { posthog, hasPostHogKey } = usePostHog()

const membershipReady = ref(false)
const deadlinePopupShown = ref(false)

const ctaTitle = computed(() => t('upgrade.ctaTitle'))
const ctaDesc = computed(() => t('upgrade.ctaDesc'))
const ctaAction = computed(() => t('upgrade.ctaAction'))

const getTodayKey = () => {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const suppressedDate = ref<string | null>(preferenceStorage.getUpgradeSuppressDate())
const isSuppressedToday = computed(() => suppressedDate.value === getTodayKey())
const showUpgradeCta = computed(
  () => membershipReady.value && !assetIsVip.value && !isSuppressedToday.value,
)

const upgradeDeadlinePopupUserId = computed(() => {
  const userInfo = userStore.user
  return String(userInfo?.sub || userInfo?.email || userInfo?.username || '').trim()
})

const goToPricingPage = (
  source: 'bottom-cta' | 'sidebar' | 'external' | 'deadline-popup' | 'route-query',
) => {
  if (hasPostHogKey) {
    posthog.capture('upgrade_cta_clicked', {
      source: source === 'bottom-cta' ? 'workbench_upgrade_cta' : source,
    })
  }
  void router.push({ name: 'ManagePricing' })
}

const handleOpenDialogClick = () => {
  goToPricingPage('bottom-cta')
}

const openDialogFromExternal = (event?: Event) => {
  const eventSource =
    event instanceof CustomEvent && event.detail?.source === 'sidebar' ? 'sidebar' : 'external'
  goToPricingPage(eventSource)
}

const maybeOpenDeadlinePopup = () => {
  if (!membershipReady.value || assetIsVip.value || deadlinePopupShown.value) return

  const userId = upgradeDeadlinePopupUserId.value
  if (!userId) return
  if (preferenceStorage.isUpgradeDeadlinePopupShown(userId, UPGRADE_DEADLINE_CAMPAIGN_ID)) {
    deadlinePopupShown.value = true
    return
  }

  deadlinePopupShown.value = true
  preferenceStorage.setUpgradeDeadlinePopupShown(userId, UPGRADE_DEADLINE_CAMPAIGN_ID)
  goToPricingPage('deadline-popup')
}

const consumeOpenUpgradeQuery = async () => {
  const shouldOpen = String(route.query.openUpgrade || '') === '1'
  if (!shouldOpen) return

  const nextQuery = { ...route.query }
  delete nextQuery.openUpgrade
  delete nextQuery.plan
  await router.replace({ path: route.path, query: nextQuery })

  goToPricingPage('route-query')
}

onMounted(() => {
  window.addEventListener(OPEN_UPGRADE_DIALOG_EVENT, openDialogFromExternal)
  if (asset.value) {
    membershipReady.value = true
    maybeOpenDeadlinePopup()
  } else if (userStore.isAuthenticated) {
    void userStore.fetchAsset().finally(() => {
      membershipReady.value = true
      maybeOpenDeadlinePopup()
    })
  } else {
    membershipReady.value = true
    maybeOpenDeadlinePopup()
  }
  void consumeOpenUpgradeQuery()
})

watch(
  [membershipReady, assetIsVip, upgradeDeadlinePopupUserId],
  () => {
    maybeOpenDeadlinePopup()
  },
  { immediate: false },
)

onBeforeUnmount(() => {
  window.removeEventListener(OPEN_UPGRADE_DIALOG_EVENT, openDialogFromExternal)
})
</script>

<style scoped>
.upgrade-cta {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid #dbeafe;
  background: linear-gradient(120deg, #ffffff, #eef2ff);
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.12);
  z-index: 1000;
  max-width: calc(100vw - 48px);
}

.upgrade-cta__copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.upgrade-cta__title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.upgrade-cta__desc {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
}

.upgrade-cta__btn {
  height: 30px;
  padding: 0 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  border-color: transparent;
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.24);
}

.upgrade-cta__btn:hover {
  background: linear-gradient(135deg, #1d4ed8, #4338ca);
  border-color: transparent;
}
</style>
